import { useFieldArray } from "react-hook-form";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import LoadingSpinner from "../../../components/LoadingSpinner";
import {
  positions,
  states,
  tshirtSizes,
} from "../../../utils/PositionStateData";

const SpotRegisterAthleteStep2 = ({
  register,
  errors,
  shouldShowError,
  watch,
  control,
  hasGraduated,
  loading,
  onBack,
}) => {
  const [showUniversities, setShowUniversities] = useState(true);

  const selectedState = watch("state");

  const { fields, append, remove } = useFieldArray({
    control,

    name: "universitiesAttended",
  });

  return (
    <>
      <h2 className="text-lg font-bold mb-2 text-white border-b pb-2 border-gray-700">
        Personal Details
      </h2>
      <div className="grid sm:grid-cols-2  gap-x-6">
        <div className="form-field mt-4">
          <label htmlFor="age">Age</label>
          <input
            type="number"
            name="age"
            min="0"
            {...register("age", {
              min: {
                value: 0,
                message: "Age cannot be negative",
              },
            })}
            placeholder="Enter your age"
          />

          {shouldShowError("age") && errors.age && (
            <span className="text-red-500 text-sm">{errors.age?.message}</span>
          )}
        </div>
        <div className="form-field mt-4">
          <label htmlFor="city">Hometown City</label>
          <input
            type="text"
            name="city"
            {...register("city", { required: "City is required" })}
            placeholder="Enter your hometown"
          ></input>

          {shouldShowError("city") && errors.city && (
            <span className="text-red-500 text-sm">{errors.city?.message}</span>
          )}
        </div>
      </div>
      <div className="grid sm:grid-cols-2  gap-x-6 mt-4">
        <div className="form-field mt-0 relative">
          <label htmlFor="state">State</label>
          <select
            id="state"
            name="state"
            className={`

      bg-gray-50 border border-gray-300 text-md rounded-md

      pr-10 appearance-none w-full

      ${selectedState ? "text-gray-900" : "text-gray-400"}

    `}
            {...register("state", { required: "State is required" })}
          >
            <option value="" disabled>
              Select your state
            </option>

            {states.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>

          {/* Arrow Icon */}
          <svg
            className="w-5 h-5 text-gray-200 absolute right-3 top-2/3 -translate-y-1/2 pointer-events-none"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>

          {shouldShowError("state") && errors.state && (
            <span className="text-red-500 text-sm">
              {errors.state?.message}
            </span>
          )}
        </div>
      </div>

      {/* Education Details*/}
      <div className="mt-8">
        <h2 className="text-lg font-bold mb-2 text-white border-b border-gray-700">
          Education Details
        </h2>
        <div className="grid sm:grid-cols-2  gap-x-6">
          <div className="form-field mt-4">
            <label htmlFor="highSchool">
              What High School did you graduate from?
            </label>
            <input
              type="text"
              name="highSchool"
              {...register("highSchool", {
                required: "High School is required",
              })}
              placeholder="Enter high school name"
            />

            {shouldShowError("highSchool") && errors.highSchool && (
              <span className="text-red-500 text-sm">
                {errors.highSchool?.message}
              </span>
            )}
          </div>
          <div className="form-field mt-4">
            <label htmlFor="gpa">GPA</label>
            <input
              type="text"
              name="gpa"
              {...register("gpa")}
              placeholder="Enter GPA"
            />

            {shouldShowError("gpa") && errors.gpa && (
              <span className="text-red-500 text-sm">
                {errors.gpa?.message}
              </span>
            )}
          </div>
        </div>
        <div className="grid sm:grid-cols-2  gap-x-6">
          <div className="form-field mt-4">
            <label htmlFor="universityOfInteres">
              Universities you are Most Interested in Playing for
            </label>
            <input
              type="text"
              name="universityOfInteres"
              {...register("universityOfInteres", {
                required: "University of interest is required",
              })}
              placeholder="Enter names of universities"
            />

            {shouldShowError("universityOfInteres") &&
              errors.universityOfInteres && (
                <span className="text-red-500 text-sm">
                  {errors.universityOfInteres?.message}
                </span>
              )}
          </div>
          <div className="form-field mt-4">
            <label htmlFor="AcademicInterest">
              Academic Degree of Interest
            </label>
            <input
              type="text"
              name="academicDegreeOfInteres"
              {...register("academicDegreeOfInteres", {
                required: "Academic Degree of Interest is required",
              })}
              placeholder="Academic Degree of Interest"
            ></input>

            {shouldShowError("academicDegreeOfInteres") &&
              errors.academicDegreeOfInteres && (
                <span className="text-red-500 text-sm">
                  {errors.academicDegreeOfInteres?.message}
                </span>
              )}
          </div>
        </div>

        {/* New Graduation Status and Major Fields */}
        <div className="grid sm:grid-cols-3 gap-x-6 mt-4">
          <div className="form-field relative">
            <label
              htmlFor="hasGraduated"
              className="block text-base text-gray-900"
            >
              Have you graduated?
            </label>
            <select
              id="hasGraduated"
              defaultValue=""
              {...register("hasGraduated", {
                setValueAs: (v) =>
                  v === "true" ? true : v === "false" ? false : false,
              })}
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>

            {shouldShowError("hasGraduated") && errors.hasGraduated && (
              <span className="text-red-500 text-sm">
                {errors.hasGraduated.message}
              </span>
            )}
          </div>
          <div className="form-field">
            <label htmlFor="major">Major</label>
            <input
              type="text"
              id="major"
              {...register("major")}
              placeholder="Major (if graduated)"
              disabled={!hasGraduated}
              className={!hasGraduated ? "bg-gray-100 cursor-not-allowed" : ""}
            />

            {shouldShowError("major") && errors.major && (
              <span className="text-red-500 text-sm">
                {errors.major?.message}
              </span>
            )}
          </div>
        </div>

        <div className="form-field pt-5">
          <label htmlFor="universitiesAttended">
            Universities Attended
            <button
              type="button"
              onClick={() => setShowUniversities(!showUniversities)}
              className="text-gray-500 hover:text-gray-700 ml-8"
            >
              <FontAwesomeIcon
                icon={showUniversities ? faChevronUp : faChevronDown}
              />
            </button>
          </label>
          {showUniversities && (
            <div className="mt-2">
              {fields.map((field, index) => (
                <div key={field.id} className="flex items-center gap-4 mb-2">
                  <div className="flex flex-col mb-2">
                    <input
                      type="text"
                      {...register(`universitiesAttended.${index}.school`, {
                        required: "University Name is required",
                      })}
                      placeholder="University Name"
                      className="border border-gray-300 rounded-md p-2 w-96"
                    />
                    {errors.universitiesAttended?.[index]?.school && (
                      <span className="text-red-500 text-sm">
                        {errors.universitiesAttended[index].school.message}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-col mb-2">
                    <input
                      type="text"
                      {...register(`universitiesAttended.${index}.year`, {
                        required: "Year is required",
                      })}
                      placeholder="Year"
                      className="border border-gray-300 rounded-md p-2 w-40"
                    />
                    {errors.universitiesAttended?.[index]?.year && (
                      <span className="text-red-500 text-sm">
                        {errors.universitiesAttended[index].year.message}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col mb-2">
                    <input
                      type="text"
                      {...register(`universitiesAttended.${index}.className`, {
                        required: "Class is required",
                      })}
                      placeholder="Class (ex: Freshman)"
                      className="border border-gray-300 rounded-md p-2 w-60"
                    />
                    {errors.universitiesAttended?.[index]?.className && (
                      <span className="text-red-500 text-sm">
                        {errors.universitiesAttended[index].className.message}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => append({ year: "", school: "", className: "" })}
                className="text-blue-500 hover:text-blue-700"
              >
                Add University
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Athletic Information*/}
      <div className="mt-8">
        <h2 className="text-lg font-bold mb-4 text-white border-b pb-2 border-gray-700 ">
          Athletic Information
        </h2>
        <div className="grid sm:grid-cols-2 gap-1 xl:gap-8 lg:gap-8 mt-4">
          <div className="form-field mt-0 relative">
            <label
              htmlFor="secondaryPosition"
              className="block text-base text-gray-900 dark:text-white"
            >
              Secondary Position
            </label>
            <select
              id="secondaryPosition"
              className={`bg-gray-50 border border-gray-300 text-md rounded-md w-full pr-10 appearance-none bg-no-repeat ${
                watch("secondaryPosition") ? "text-gray-900" : "text-gray-400"
              }`}
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg fill='none' stroke='%236b7280' stroke-width='3' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E\")",

                backgroundPosition: "right 0.75rem center",

                backgroundSize: "1.1rem",

                backgroundRepeat: "no-repeat",
              }}
              {...register("secondaryPosition")}
            >
              <option value="" disabled className="text-white bg-gray-800">
                None
              </option>

              {positions.map((position) => (
                <option
                  key={position}
                  value={position}
                  className="text-white bg-gray-800"
                >
                  {position === "Interior Offensive Line"
                    ? "Interior Offensive Line (Guard or Center)"
                    : position}
                </option>
              ))}
            </select>

            {shouldShowError("secondaryPosition") &&
              errors.secondaryPosition && (
                <span className="text-red-500 text-sm">
                  {errors.secondaryPosition.message}
                </span>
              )}
          </div>
          <div className="form-field mt-0 col-span-2">
            <label htmlFor="tShirtSize">T-Shirt Size</label>
            <select
              id="tShirtSize"
              {...register("tShirtSize", {
                required: "T-Shirt Size is required",
              })}
              className={`bg-gray-50 border border-gray-300 text-md rounded-md w-full pr-10 appearance-none bg-no-repeat ${
                watch("secondaryPosition") ? "text-gray-900" : "text-gray-400"
              }`}
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg fill='none' stroke='%236b7280' stroke-width='3' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E\")",

                backgroundPosition: "right 0.75rem center",

                backgroundSize: "1.1rem",

                backgroundRepeat: "no-repeat",
              }}
            >
              <option value="" disabled className="text-gray-400">
                Select T-Shirt Size
              </option>

              {tshirtSizes.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            {errors.tShirtSize && (
              <span className="text-red-500">{errors.tShirtSize.message}</span>
            )}
          </div>
        </div>

        {/* New Eligibility Section */}
        <div className="grid sm:grid-cols-2 gap-1 xl:gap-8 lg:gap-8 mt-4">
          <div className="form-field mt-0 relative">
            <label
              htmlFor="yearsLeftToPlay"
              className="block text-base text-gray-900"
            >
              Years of eligibility left
            </label>
            <select
              id="yearsLeftToPlay"
              {...register("yearsLeftToPlay")}
              className="bg-gray-50 border border-gray-300 text-md rounded-md

     form-selectw appearance-none bg-no-repeat text-gray-900

     w-full pr-10"
              defaultValue=""
            >
              <option value="" disabled className="text-gray-400">
                Select eligibility
              </option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
            </select>

            {/* Dropdown Arrow */}
            <svg
              className="w-5 h-5 text-gray-200 absolute right-3 top-[65%] -translate-y-1/2 pointer-events-none"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>

            {shouldShowError("yearsLeftToPlay") && errors.yearsLeftToPlay && (
              <span className="text-red-500 text-sm">
                {errors.yearsLeftToPlay.message}
              </span>
            )}
          </div>
          <div className="form-field mt-0 relative">
            <label
              htmlFor="redShirtAvailable"
              className="block text-base text-gray-900"
            >
              Redshirt Available?
            </label>
            <select
              id="redShirtAvailable"
              {...register("redShirtAvailable")}
              className="bg-gray-50 border border-gray-300 text-md rounded-md

               form-selectw appearance-none bg-no-repeat text-gray-900

               w-full pr-10"
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>

            {/* Dropdown Arrow */}
            <svg
              className="w-5 h-5 text-gray-200 absolute right-3 top-[65%] -translate-y-1/2 pointer-events-none"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>

            {shouldShowError("redShirtAvailable") &&
              errors.redShirtAvailable && (
                <span className="text-red-500 text-sm">
                  {errors.redShirtAvailable.message}
                </span>
              )}
          </div>
        </div>
      </div>

      {/* Person helping you most with your Decision*/}
      <div className="mt-8">
        <h2 className="text-lg font-bold mb-2 text-white border-b pb-2 border-gray-700">
          Person helping you most with your Decision
        </h2>
        <div className="grid sm:grid-cols-3 gap-1 xl:gap-8 lg:gap-8 mt-4">
          <div className="form-field">
            <label htmlFor="personHelpingYouName">Name</label>
            <input
              type="text"
              id="personHelpingYouName"
              autoComplete="off"
              {...register("personHelpingYouName")}
              placeholder="Enter advisor's name"
            />

            {shouldShowError("personHelpingYouName") &&
              errors.personHelpingYouName && (
                <span className="text-red-500 text-sm">
                  {errors.personHelpingYouName?.message}
                </span>
              )}
          </div>
          <div className="form-field">
            <label htmlFor="personHelpingYouRelationship">
              Relationship to you
            </label>
            <input
              type="text"
              id="personHelpingYouRelationship"
              {...register("personHelpingYouRelationship")}
              placeholder="Enter relationship"
            />

            {shouldShowError("personHelpingYouRelationship") &&
              errors.personHelpingYouRelationship && (
                <span className="text-red-500 text-sm">
                  {errors.personHelpingYouRelationship?.message}
                </span>
              )}
          </div>
          <div className="form-field">
            <label htmlFor="personHelpingYouPhone">Phone Number</label>
            <input
              type="tel"
              id="personHelpingYouPhone"
              inputMode="numeric"
              pattern="[0-9]{10}"
              placeholder="Enter phone number"
              maxLength={10}
              {...register("personHelpingYouPhone", {
                pattern: {
                  value: /^[0-9]{10}$/,

                  message: "Enter a valid 10-digit phone number",
                },
              })}
              className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onInput={(e) => {
                e.target.value = e.target.value.replace(/\D/g, "");
              }}
            />

            {shouldShowError("personHelpingYouPhone") &&
              errors.personHelpingYouPhone && (
                <span className="text-red-500 text-sm">
                  {errors.personHelpingYouPhone?.message}
                </span>
              )}
          </div>
        </div>
      </div>
      <div className="mt-10 p-3 rounded-md bg-[#2d2d2d]">
        <h2 className="text-base font-semibold mb-2 text-white">
          Waiver & Liability Agreement
        </h2>
        {/* Scrollable Text Box */}
        <div className="h-40 overflow-y-scroll border border-gray-600 p-3 bg-black rounded-md text-sm leading-6 text-white">
          <p className="text-sm">
            I hereby acknowledge that I am, or my child is, voluntarily
            participating in a Combine organized by Track It Verified, LLC (“the
            Activity”). In consideration for being permitted to participate in
            this activity, I hereby agree as follows:
          </p>
          <ol className="list-decimal list-inside text-sm mt-2">
            <li className="pb-3">
              {" "}
              I understand and acknowledge that there are certain risks and
              dangers associated with the Activity, including but not limited
              to: getting injured while performing the combine drills.{" "}
            </li>
            <li className="pb-3">
              On behalf of myself (if 18 or older) or on behalf of my child,
              I/we voluntarily assume all risks related to participation in the
              Activity and agree to release and hold harmless Track It Verified,
              LLC, its officers, employees, volunteers, and agents from any and
              all claims, damages, liabilities, costs, and expenses that may
              arise as a result of participation in the Activity, including but
              not limited to injury or loss.{" "}
            </li>
            <li className="pb-3">
              On behalf of myself (if 18 or older) or on behalf of my child,
              I/we agree to indemnify and defend Track It Verified, LLC, its
              officers, employees, volunteers, and agents against any and all
              claims, damages, liabilities, costs, and expenses, including
              attorney fees, arising from or in connection with participation in
              the Activity.{" "}
            </li>
            <li className="pb-3">
              On behalf of myself (if 18 or older) or on behalf of my child,
              I/we certify that I or my child is in good health and have/has no
              physical or mental conditions that prevent me or my child from
              safely participating in the Activity.{" "}
            </li>
            <li className="pb-3">
              On behalf of myself (if 18 or older) or on behalf of my child, I
              grant Track It Verified, LLC the right to use photographs, video
              recordings, or other media of me or my child taken during the
              Activity for promotional or marketing purposes. If I am signing
              this on behalf of my child, I understand that Pursuant to the
              Children’s Online Policy Protection Act (“COPPA”), Track It
              Verified, LLC, located at 1899 Oak Lane, Provo, Utah 84604, wishes
              to collect personal information from my child. The “personal
              information” of the Child includes, images, photographs, video
              recordings, name acknowledgment, video tapings, statistics,
              height, weight and other information pertaining to the Child while
              participating in the Activity. I understand that Parent/Guardian
              consent is required for the collection, use and disclosure of the
              child's personal information. I hereby acknowledge that I am the
              Parent/Guardian of the child listed above and consent to Track It
              Verified, LLC's collection, use, and disclosure of the child's
              personal information by signing this document. I understand that I
              have the option to consent to the collection and use of the
              child's personal information without consenting to disclosure of
              the child's information to third parties. I understand that my
              failure to consent to Track It Verified, LLC's collection use and
              disclosure of the child's personal information will not prevent
              the child from participating in the Activity. Track It Verified,
              LLC, will not require a child to disclose more information than is
              reasonably necessary to participate in the Activity as a condition
              of participation. Parent/Guardian can review the child's personal
              information, ask to have it deleted, and refuse to allow any
              further collection use or disclosure of the child's information.
              Track It Verified, LLC has established and maintains reasonable
              procedures to protect the security and integrity of the personal
              information collected from the child. If the Parent/Guardian does
              not wish to provide consent for Track It Verified, LLC's
              collection, use, and disclosure of the child's personal
              information or seeks to revoke previously granted consent,
              Parent/Guardian must contact Track It Verified, LLC at:
              trackittverified@gmail.com and make this request directly.
              Parent/Guardian releases Track It Verified, LLC, its employees,
              contractors, and each and all persons and parties involved from
              any liability connected with the taking, recording, or publication
              of said photographs, video, or computer images involving the
              Child. Parent/Guardian waives all rights he/she may have to any
              claims for any payment or royalties in connection with any
              exhibition, televising, or other publication of these materials,
              regardless of the purpose or sponsoring of such exhibiting,
              broadcasting, or other publication. All negatives and positives,
              whether prints, digital video, film or video, are the exclusive
              property of Track It Verified, LLC or the person or entity
              designated by it, solely and completely.
            </li>
          </ol>
          <p className="mt-1 text-sm">
            I HAVE READ THIS RELEASE, IF SIGNING MYSELF OR ON BEHALF OF MY
            CHILD, AND FULLY UNDERSTAND ITS TERMS, AND UNDERSTAND THAT I AM
            SIGNING FREELY AND VOLUNTARILY WITHOUT ANY INDUCEMENT.
          </p>
        </div>

        {/* Checkbox Requirement */}
        <div className="flex items-center gap-2 my-3">
          <input
            type="checkbox"
            {...register("agreedToWaiver", { required: true })}
            className="w-4 h-4"
          />
          <label className="text-sm text-white">
            I have read and agree to the Waiver & Liability Agreement.
          </label>
        </div>

        {errors.agreedToWaiver && (
          <p className="text-red-500 text-xs mt-1">
            You must accept the waiver before registering.
          </p>
        )}
      </div>
      <div className="px-2 my-10">
        <div className="form-field mt-4">{loading && <LoadingSpinner />}</div>
        <div className="flex gap-4">
          <button
            type="button"
            onClick={onBack}
            disabled={loading}
            className="bg-gray-600 text-white uppercase rounded-full px-8 py-3 text-center mt-4 hover:bg-gray-700 cursor-pointer w-1/3 disabled:opacity-50"
          >
            Back to Step 1
          </button>
          <button
            type="submit"
            disabled={loading}
            className="bg-red-500 text-white uppercase rounded-full px-8 py-3 text-center mt-4 hover:bg-red-700 cursor-pointer w-full disabled:opacity-50"
          >
            Registration
          </button>
        </div>
      </div>
    </>
  );
};

export default SpotRegisterAthleteStep2;
